import gatewayService from '@/modules/common/services/gatewayService';
import httpService from '@/modules/common/services/httpService';
import logError from '@/utils/logError/logError';

const BASE = 'v3/online_presence/widget_configurations';
const CONVERSATIONS_BASE = 'v3/online_presence/chat_widget_conversations';
const LEGACY_ACTIVE_ENGAGE_BASE = 'business/web_presence/v2/active_engage_widget';

/**
 * Normalize API response to UI shape: uid → id, business_uid → business_id; build synthetic business.
 * @param {object} data - Raw widget_configuration from API (uid, business_uid, + config fields)
 * @returns {{ config: object, business: { id: string, name: string } }}
 */
function normalizeResponse(data) {
  if (!data) {
    return { config: null, business: { id: '', name: '' } };
  }
  // Destructure top-level fields so they are not duplicated; business_name must be on config
  // so the UI shows the saved custom title after load/navigation (see WebAIWidgetConfigPanel).
  const { uid, business_uid: bizUid, business_name: bizName, ...rest } = data;
  const config = {
    id: uid,
    business_id: bizUid,
    business_name: bizName,
    ...rest,
  };
  const business = {
    id: bizUid ?? '',
    name: bizName ?? '',
  };
  return { config, business };
}

/**
 * Get main widget configuration for a business. Returns null config when none exists (404).
 * @param {string} businessUid - Business UID (from AuthStore)
 * @returns {Promise<{ config: object | null, business: { id: string, name: string } }>}
 */
async function getMainConfig(businessUid) {
  try {
    const res = await gatewayService.get(`${BASE}/main`, {
      business_uid: businessUid, // API expects snake_case
    });
    const raw = res?.data?.data?.widget_configuration ?? res?.data?.widget_configuration;
    return normalizeResponse(raw);
  } catch (err) {
    if (err?.response?.status === 404) {
      return {
        config: null,
        business: { id: businessUid, name: '' },
      };
    }
    throw err;
  }
}

/**
 * Upsert main widget configuration for a business.
 * @param {string} businessUid - Business UID (from AuthStore)
 * @param {object} payload - Config fields only (no id, uid, business_id); snake_case
 * @returns {Promise<{ config: object, business: { id: string, name: string } }>}
 */
async function upsertMainConfig(businessUid, payload) {
  const body = {
    business_uid: businessUid, // API expects snake_case
    ...payload,
  };
  const res = await gatewayService.put(`${BASE}/main`, body);
  const raw = res?.data?.data?.widget_configuration ?? res?.data?.widget_configuration;
  return normalizeResponse(raw);
}

/**
 * GET v3/online_presence/widget_configurations/public/:widget_uid — same resolved snapshot as embed/widget.
 * Used by POV style tab so empty stored fields match public JSON byte-for-byte.
 * @param {string} widgetUid - Widget configuration uid (same as config.id from main)
 * @returns {Promise<object|null>} Resolved public DTO (snake_case) or null if request fails
 */
async function getPublicResolvedConfig(widgetUid) {
  if (!widgetUid || !String(widgetUid).trim()) {
    return null;
  }
  const res = await gatewayService.get(
    `${BASE}/public/${encodeURIComponent(String(widgetUid).trim())}`,
  );
  const raw = res?.data?.data ?? res?.data;
  return raw && typeof raw === 'object' ? raw : null;
}

function parseActionsResponse(res) {
  const body = res?.data ?? {};
  let actions = [];
  if (Array.isArray(body.data)) {
    actions = body.data;
  } else if (Array.isArray(body.data?.data)) {
    actions = body.data.data;
  } else if (Array.isArray(body.data?.actions)) {
    actions = body.data.actions;
  } else if (Array.isArray(body.actions)) {
    actions = body.actions;
  } else if (Array.isArray(body)) {
    actions = body;
  }
  return actions.map((a, index) => {
    const key = String(a.name ?? a.key ?? a.type ?? '').toLowerCase() || 'action';
    const defaultLabel = a.title ?? a.label ?? a.rich_title ?? a.display_name ?? a.default_title ?? key;
    const uid = typeof a.uid === 'string' && a.uid.trim() ? a.uid.trim() : null;
    return {
      uid,
      key,
      defaultLabel,
      icon: a.icon ?? null,
      // Keep deterministic order for duplicate action types (e.g. two `schedule` actions).
      order: typeof a.position === 'number' ? a.position : index,
    };
  }).sort((a, b) => a.order - b.order);
}

/**
 * Get the list of client portal actions for this business (same as Edit portal actions dialog).
 * Tries core API first; on failure or empty, falls back to same-origin (api2) so labels always load.
 * @param {string} businessUid - Business UID (from AuthStore)
 * @returns {Promise<Array<{ uid: string|null, key: string, defaultLabel: string, icon: string|null, order: number }>>}
 */
async function getAvailablePortalActions(businessUid) {
  if (!businessUid) return [];
  const ACTIONS_PATH = 'business/web_presence/v2/actions/';
  const params = { context: 'client_portal', new_api: true, teams_view_filter: '{}' };
  try {
    const res = await gatewayService.get(ACTIONS_PATH, params);
    const list = parseActionsResponse(res);
    if (list.length > 0) return list;
  } catch (err) {
    logError('Failed to fetch portal actions via gateway', err, { businessUid });
  }
  try {
    const res = await httpService.get(ACTIONS_PATH, params);
    return parseActionsResponse(res);
  } catch (err) {
    logError('Failed to fetch portal actions via httpService fallback', err, { businessUid });
    return [];
  }
}

/**
 * List conversations for the logged-in business (backend derives business from auth; do not pass business_uid).
 * @param {object} params - { page?: number, per_page?: number, query?: string }
 * @returns {Promise<{ conversations: Array<object>, total: number }>}
 */
async function getConversations(params = {}) {
  const {
    page = 1,
    per_page: perPage = 25,
    query,
    sort = 'started_at:desc',
  } = params;
  const requestParams = { page, per_page: perPage, sort };
  if (query != null && String(query).trim() !== '') {
    requestParams.q = String(query).trim();
  }
  const res = await gatewayService.get(CONVERSATIONS_BASE, requestParams);
  const data = res?.data?.data ?? res?.data;
  const conversations = Array.isArray(data?.conversations) ? data.conversations : [];
  const total = typeof data?.total === 'number' ? data.total : 0;
  return { conversations, total };
}

/**
 * Get a single conversation by uid (for transcript modal). Backend scopes to logged-in business.
 * @param {string} uid - Conversation UID
 * @returns {Promise<object>} Conversation object (uid, transcript, summary, etc.)
 */
async function getConversation(uid) {
  const res = await gatewayService.get(`${CONVERSATIONS_BASE}/${uid}`);
  const conversation = res?.data?.data ?? res?.data;
  return conversation ?? null;
}

/**
 * GET v2/weekly_availabilities/:uid (general availability) via APIGW. Used for default business hours.
 * @param {string} weeklyAvailabilityUid - weekly_availability_uid from owner staff
 * @returns {Promise<{ time_slots_by_day: Array }>} Response with time_slots_by_day
 */
async function getWeeklyAvailabilityGeneral(weeklyAvailabilityUid) {
  if (!weeklyAvailabilityUid || !weeklyAvailabilityUid.trim()) {
    return null;
  }
  const res = await httpService.get(`/v2/weekly_availabilities/${weeklyAvailabilityUid.trim()}`, {
    general_availability: 'true',
    use_selected_staff_time_zone: 'true',
  });
  const data = res?.data ?? res;
  return data ?? null;
}

/**
 * Get legacy Active Engage widget settings needed for migration toggle state.
 * Authenticated same-origin endpoint.
 * @returns {Promise<{ sub_widget_type: string|null }>}
 */
async function getLegacyActiveEngageSettings() {
  const res = await gatewayService.get(LEGACY_ACTIVE_ENGAGE_BASE);
  const data = res?.data?.data ?? res?.data ?? {};
  return {
    sub_widget_type: typeof data?.sub_widget_type === 'string' ? data.sub_widget_type : null,
  };
}

/**
 * Persist legacy Active Engage migration payload in sub_widget_type.
 * @param {string|null} subWidgetType
 * @returns {Promise<void>}
 */
async function updateLegacyActiveEngageSettings(subWidgetType) {
  await gatewayService.put(LEGACY_ACTIVE_ENGAGE_BASE, {
    sub_widget_type: subWidgetType,
  });
}

export default {
  getMainConfig,
  upsertMainConfig,
  getPublicResolvedConfig,
  getAvailablePortalActions,
  getConversations,
  getConversation,
  getWeeklyAvailabilityGeneral,
  getLegacyActiveEngageSettings,
  updateLegacyActiveEngageSettings,
};
